import { FC, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBottomAligned } from '../useBottomAligned';
import { Box, IconButton, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Page } from '@api/models/common/Page';
import { AnalyticsRowDto } from '@api/models/analytics/AnalyticsRowDto';
import { QcsTablePagination } from '../../common/basic/QcsTablePagination';
import { RightDrawer } from '../../common/RightDrawer';
import { ColumnMetaDto } from '@api/models/analytics/ColumnMetaDto';
import { ClickableRow, ScrollArea, StickyHeadCell, TableEmpty, TableRoot } from './AnalyticsRowsTableStyles';
import { formatCellValue, getColumnAlign } from '../analyticsColumns';

interface Props {
  page?: Page<AnalyticsRowDto>;
  columns: ColumnMetaDto[];
  rowsPerPage: number;
  rowsPerPageDisabled?: boolean;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
  loading?: boolean;
  empty?: boolean;
  getRowHref?: (row: AnalyticsRowDto) => string | undefined;
  getRowDrawerId?: (row: AnalyticsRowDto) => string | undefined;
  DrawerBodyComponent?: FC<{ id: string }>;
}

export const AnalyticsRowsTable = ({
  page,
  columns,
  rowsPerPage,
  rowsPerPageDisabled,
  onPageChange,
  onSizeChange,
  loading,
  empty,
  getRowHref,
  getRowDrawerId,
  DrawerBodyComponent,
}: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const rootRef = useBottomAligned<HTMLDivElement>(true);
  // Drawer open state is intentionally independent of the table data: the user keeps
  // seeing the drilled-in record even if a filter/page change hides it from the table.
  // The id is mirrored to the URL as `?drawerId=…` so a back-navigation reopens it.
  const [openId, setOpenId] = useState<string | undefined>(
    () => new URLSearchParams(location.search).get('drawerId') ?? undefined,
  );

  const rows = useMemo(() => page?.content ?? [], [page?.content]);

  // Derive the fullscreen href from the currently rendered rows. If the row that
  // matches `openId` is not on the current page (e.g. drawer was restored from URL
  // before rows loaded, or user changed page), the fullscreen icon is hidden.
  const resolvedHref = useMemo(() => {
    if (!openId || !getRowHref) return undefined;
    const row = rows.find((r) => getRowDrawerId?.(r) === openId);
    return row ? getRowHref(row) : undefined;
  }, [openId, rows, getRowHref, getRowDrawerId]);

  // Mirror the drawer's open id to the URL so back-navigation can restore it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get('drawerId');
    const desired = openId ?? null;
    if (current === desired) return;
    if (desired) params.set('drawerId', desired);
    else params.delete('drawerId');
    navigate({ search: `?${params.toString()}` }, { replace: true });
  }, [openId, location.search, navigate]);

  if (loading) {
    return (
      <TableRoot ref={rootRef}>
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: '12px' }} />
      </TableRoot>
    );
  }

  if (empty || rows.length === 0) {
    return (
      <TableRoot ref={rootRef}>
        <TableEmpty>{t('analytics.table.empty')}</TableEmpty>
      </TableRoot>
    );
  }

  const renderCells = (row: AnalyticsRowDto) =>
    columns.map((col) => (
      <TableCell key={col.key} align={getColumnAlign(col.type)}>
        {formatCellValue(row.values?.[col.key], col.type, i18n)}
      </TableCell>
    ));

  const handleKeyDown = (handler: () => void) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };

  const navigateWithBackTo = (href: string) => {
    const backTo = `${location.pathname}${location.search}`;
    const sep = href.includes('?') ? '&' : '?';
    navigate(`${href}${sep}backTo=${encodeURIComponent(backTo)}`);
  };

  const handleFullscreen = () => {
    if (resolvedHref) {
      navigateWithBackTo(resolvedHref);
    }
  };

  return (
    <TableRoot ref={rootRef}>
      <ScrollArea>
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <StickyHeadCell key={col.key} align={getColumnAlign(col.type)}>
                  {t(col.labelKey)}
                </StickyHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const drawerId = getRowDrawerId?.(row);
              const href = getRowHref?.(row);
              const rowKey = drawerId ?? href ?? `p${page?.number ?? 0}-${idx}`;

              const onActivate =
                drawerId !== undefined && DrawerBodyComponent
                  ? () => setOpenId(drawerId)
                  : href !== undefined
                    ? () => navigateWithBackTo(href)
                    : undefined;

              if (!onActivate) {
                return <TableRow key={rowKey}>{renderCells(row)}</TableRow>;
              }

              return (
                <ClickableRow key={rowKey} role="button" tabIndex={0} onClick={onActivate} onKeyDown={handleKeyDown(onActivate)}>
                  {renderCells(row)}
                </ClickableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
      <QcsTablePagination
        count={page?.totalElements ?? 0}
        page={page?.number ?? 0}
        rowsPerPage={rowsPerPage}
        rowsPerPageDisabled={rowsPerPageDisabled}
        onPageChange={onPageChange}
        onRowsPerPageChange={onSizeChange}
      />
      <RightDrawer
        open={openId !== undefined}
        onClose={() => setOpenId(undefined)}
        width={800}
        headerActions={
          resolvedHref ? (
            <IconButton onClick={handleFullscreen} aria-label={t('analytics.table.openFullPage')}>
              <OpenInFullIcon />
            </IconButton>
          ) : undefined
        }
      >
        {DrawerBodyComponent && openId !== undefined ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <DrawerBodyComponent id={openId} />
          </Box>
        ) : null}
      </RightDrawer>
    </TableRoot>
  );
};
